// ============================================================
// useDefenceProbability - Debounced defence probability hook.
// Recalculates when target, defender mods, or attacker config changes.
// ============================================================
import { useMemo } from 'react';
import { computeDefenceProbabilities } from '../engine/defenceProbability';
import type { DefenceProbabilityInput, DefenceProbabilityResult } from '../engine/defenceProbability';
import { DEFAULT_UNNATURAL_DOS_MODE, type UnnaturalDosMode } from '../engine/unnaturalDos';
import type { Target, Slot, DefenderCustomMod } from '../types';
import type { ConditionModContext, ConditionModOptions } from '../engine/conditionModifiers';
import { useDebouncedResult } from './useDebouncedResult';

interface UseDefenceProbabilityParams {
  target: Target | null;
  defenderCustomMods: DefenderCustomMod[];
  slot?: Slot | null;
  fireMode?: string;
  isMelee?: boolean;
  attackMod?: number;
  rangeBand?: string;
  hatred?: boolean;
  damageBonusMelee?: number;
  damageBonusRanged?: number;
  /** The attacker's team tag, for strict exposure scopes (v8.368.0). */
  attackerTeamTag?: string | null;
  /** The attacker's side (2026-10-23); absent = the tag. */
  attackerSideTag?: string | null;
  /** The attacker's magnitude when it is a horde: +1 hit per 10, as the roll (ruling 2026-10-09). */
  attackerMagnitude?: number;
  /** The attacker's reaction exposures against this defender (v8.368.3). */
  attackerReactionMods?: { dodge: number; parry: number } | null;
  /** The attacker as the roll reads them - conditions and the auras they stand under (2026-10-20). */
  attackerConditions?: ConditionModContext | null;
  /** Unnatural DoS house rule - forecasts must mirror the live math. */
  unnaturalDosMode?: UnnaturalDosMode;
  skillCharValue?: number;
  skillUnnaturalMultiplier?: number;
  /** Condition-modifier house rule - forecasts must mirror the live defence chain. */
  applyConditionModifiers?: boolean;
  /** Which condition modifiers the table has switched off. */
  condModOptions?: ConditionModOptions;
  /** The attacker's re-roll damage pill (v8.392.4). */
  rerollDamage?: boolean;
}

interface UseDefenceProbabilityReturn {
  result: DefenceProbabilityResult | null;
  loading: boolean;
}

/** The engine's input for this defender against the live attack. */
function defenceInput(p: UseDefenceProbabilityParams & { target: Target }): DefenceProbabilityInput {
  return {
    target: p.target,
    defenderCustomMods: p.defenderCustomMods,
    slot: p.slot ?? null,
    fireMode: p.fireMode,
    isMelee: p.isMelee,
    attackMod: p.attackMod,
    rangeBand: p.rangeBand,
    hatred: p.hatred,
    damageBonusMelee: p.damageBonusMelee ?? 0,
    damageBonusRanged: p.damageBonusRanged ?? 0,
    attackerSpecials: p.slot?.specialRules || [],
    attackerTeamTag: p.attackerTeamTag ?? null,
    attackerSideTag: p.attackerSideTag ?? null,
    attackerMagnitude: p.attackerMagnitude ?? 0,
    attackerReactionMods: p.attackerReactionMods ?? null,
    ...(p.attackerConditions ? { attackerConditions: p.attackerConditions } : {}),
    unnaturalDosMode: p.unnaturalDosMode ?? DEFAULT_UNNATURAL_DOS_MODE,
    skillCharValue: p.skillCharValue ?? 0,
    skillUnnaturalMultiplier: p.skillUnnaturalMultiplier ?? 1,
    applyConditionMods: p.applyConditionModifiers ?? false,
    condOptions: p.condModOptions,
    rerollDamage: p.rerollDamage ?? false,
  };
}

/** Debounced dodge / parry / survival forecast for a defender. Nothing without a target. */
export default function useDefenceProbability(params: UseDefenceProbabilityParams): UseDefenceProbabilityReturn {
  const { target, defenderCustomMods, slot, fireMode, isMelee, attackMod, rangeBand, hatred } = params;
  const { damageBonusMelee, damageBonusRanged, attackerTeamTag, attackerReactionMods, unnaturalDosMode } = params;
  const { attackerSideTag } = params;
  const { attackerMagnitude, attackerConditions } = params;
  const { skillCharValue, skillUnnaturalMultiplier, applyConditionModifiers, condModOptions, rerollDamage } = params;
  // Long only because the input has this many fields: the memo lists what it reads.
  const input = useMemo(
    () =>
      target
        ? defenceInput({
            target,
            defenderCustomMods,
            slot,
            fireMode,
            isMelee,
            attackMod,
            rangeBand,
            hatred,
            damageBonusMelee,
            damageBonusRanged,
            attackerTeamTag,
            attackerSideTag,
            attackerMagnitude,
            attackerReactionMods,
            attackerConditions,
            unnaturalDosMode,
            skillCharValue,
            skillUnnaturalMultiplier,
            applyConditionModifiers,
            condModOptions,
            rerollDamage,
          })
        : null,
    [
      target,
      defenderCustomMods,
      slot,
      fireMode,
      isMelee,
      attackMod,
      rangeBand,
      hatred,
      damageBonusMelee,
      damageBonusRanged,
      attackerTeamTag,
      attackerSideTag,
      attackerMagnitude,
      attackerReactionMods,
      attackerConditions,
      unnaturalDosMode,
      skillCharValue,
      skillUnnaturalMultiplier,
      applyConditionModifiers,
      condModOptions,
      rerollDamage,
    ],
  );
  return useDebouncedResult(input, computeDefenceProbabilities);
}
